/* Our Story — "alive" layer (Oct 2026). v7, 14 Oct: the bee flies freely.
   1. The crest bee keeps the reader company. Instead of riding a fixed track tied to the scroll,
      she flies on her own: she always heads for a free spot near the line you are reading and
      steers there with a real bee's momentum (she speeds up, slows down, banks and turns, and
      hovers gently when she arrives).
      - She never flies over words. The page is mapped into small cells; every cell where her
        drawing would touch a line of text is blocked, and she plans her way around them
        (through the margins, over the photos, or through the gaps between blocks). A final check
        on every frame stops her from ever entering a blocked cell.
      - Where she goes: beside a step's number when it reaches the reading line, onto a photo while
        the photo is at the reading line, and otherwise down the margin beside the text (the gold
        frame rail). On phones she is sized to fit that margin.
      - If the margin is ever too narrow to pass, she may fly out past the screen edge and come
        back in (a short corridor beyond the edge), never across the text.
   2. Character blocks pop in when they enter the screen and drift with the scroll.
   Transform + opacity only. One rAF loop while the section is on screen.
   Reduced motion: everything shown in place, the bee resting beside the title. */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.__osyAlive) return;
    root.__osyAlive = true;

    var inner = root.querySelector('.osy-inner');
    var fly = root.querySelector('[data-osy-fly]');
    var bee = root.querySelector('[data-osy-bee]');
    var trail = root.querySelector('[data-osy-trail]');
    var wake = root.querySelector('[data-osy-wake]');
    var fxTw = fly ? (fly.getAttribute('data-fx-twinkles') || '').split(',').filter(Boolean) : [];
    var fxStar = fly ? fly.getAttribute('data-fx-star') : '';
    var chars = Array.prototype.slice.call(root.querySelectorAll('.osy-ch'));
    var RTL = (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
    var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    var reduce = mq.matches;
    var baseBee = fly ? parseFloat(fly.style.getPropertyValue('--bee')) || 110 : 110;
    if (trail) trail.style.display = 'none'; // the fixed dotted route belongs to the old track; she leaves sparkles instead

    /* ---------- character reveal ---------- */
    var visible = new Set();
    if ('IntersectionObserver' in window) {
      var cio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); visible.add(e.target); }
          else { visible.delete(e.target); }
        });
        kick();
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
      chars.forEach(function (c) { cio.observe(c); });
    } else {
      chars.forEach(function (c) { c.classList.add('is-in'); visible.add(c); });
    }
    if (reduce) chars.forEach(function (c) { c.classList.add('is-in'); });

    /* ---------- page map ---------- */
    var pose = bee ? (bee.getAttribute('data-pose') || 'front') : 'front';
    var TAIL = { x: -0.313, y: 0.23 }; // tail tip of the crest bee, from the image centre (share of width / height)
    var INK = { w: 0.9, h: 0.95 };      // share of the bee picture that is actually drawn
    var beeSize = 0, beeH = 0, W = 0, ready = false, narrow = false;
    var grid = null, rows = [], hero = null, titleBox = null, screenL = 0, screenR = 0;

    function rel(el) {
      var r = el.getBoundingClientRect(), b = inner.getBoundingClientRect();
      return { x: r.left - b.left, y: r.top - b.top, w: r.width, h: r.height, r: r.right - b.left, b: r.bottom - b.top };
    }
    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

    // every line of text on the page, as boxes relative to the content column
    function textLines() {
      var sel = '.osy-head .osy-eyebrow, .osy-title, .osy-intro p, .osy-num, .osy-row-title, .osy-row-body, .osy-close .osy-eyebrow, .osy-close-title, .osy-close-body';
      var ib = inner.getBoundingClientRect(), out = [];
      Array.prototype.forEach.call(root.querySelectorAll(sel), function (el) {
        var rg = document.createRange(); rg.selectNodeContents(el);
        Array.prototype.forEach.call(rg.getClientRects(), function (r) {
          if (r.width < 1 || r.height < 1) return;
          out.push({ x0: r.left - ib.left, x1: r.right - ib.left, y0: r.top - ib.top, y1: r.bottom - ib.top });
        });
      });
      return out;
    }

    function measure() {
      if (!bee || !inner) return;
      W = inner.clientWidth;
      var ib = inner.getBoundingClientRect();
      var vw = document.documentElement.clientWidth;
      screenL = -ib.left; screenR = vw - ib.left;
      var gutter = Math.min(ib.left, vw - ib.right);
      narrow = window.innerWidth <= 860;

      // size: as large as the setting, but small enough to pass down the margin beside the words
      // wide screens: sized to pass down the margin beside the words; phones keep their size and,
      // where there is no margin, slip out past the screen edge for a moment to pass a block of text
      var want = narrow ? baseBee * 0.52 : baseBee;
      var size = narrow ? want : clamp(Math.min(want, (gutter - 34) / INK.w), 60, want);
      if (fly) fly.style.setProperty('--bee', (size / (narrow ? 0.6 : 1)).toFixed(1) + 'px');
      beeSize = bee.offsetWidth || size;
      beeH = bee.offsetHeight || beeSize * 1.5;

      rows = Array.prototype.slice.call(root.querySelectorAll('.osy-row')).map(function (row) {
        var t = row.querySelector('.osy-row-text'), m = row.querySelector('.osy-row-media'), n = row.querySelector('.osy-num');
        if (!t || !m) return null;
        var tr = rel(t), mr = rel(m), line = rel(n ? (n.querySelector('i') || n) : t);
        return { text: tr, media: mr, num: n, numY: line.y + line.h / 2, textLeft: tr.x + tr.w / 2 < mr.x + mr.w / 2 };
      }).filter(Boolean);
      var h = root.querySelector('.osy-hero-wrap'); hero = h ? rel(h) : null;
      var tl = root.querySelector('.osy-title'); titleBox = tl ? rel(tl) : null;

      buildGrid(textLines());
      ready = !!grid;
      if (fly) fly.classList.toggle('is-ready', ready);
    }

    // Cells of the page; a cell is blocked when the bee centred there would touch a line of text.
    var MAXT = 16; // largest lean in degrees (the drawing is tall, so leaning swings its top sideways)
    function buildGrid(lines) {
      var cs = narrow ? 10 : 14;
      MAXT = narrow ? 12 : 16;
      var lean = beeH * 0.5 * Math.sin(MAXT * Math.PI / 180);
      // hard = her drawing would touch the line (never entered); soft = a little extra room kept when planning
      var hwH = beeSize * INK.w / 2 + lean * 0.8 + 2, hhH = beeH * INK.h / 2 + 10;
      var hwS = hwH + 3, hhS = hhH + 8;
      // a little room past the screen edges: on a phone she can slip along the edge, half in view,
      // to get past a block of words (never fully out of sight for long)
      var corridor = beeSize * 0.3;
      var x0 = screenL - corridor, x1 = screenR + corridor;
      var y0 = -beeH, y1 = inner.scrollHeight + beeH * 0.5;
      var cols = Math.ceil((x1 - x0) / cs), rowsN = Math.ceil((y1 - y0) / cs);
      var block = new Uint8Array(cols * rowsN), cost = new Float32Array(cols * rowsN);
      function mark(l, hw, hh, v) {
        var c0 = Math.max(0, Math.floor((l.x0 - hw - x0) / cs)), c1 = Math.min(cols - 1, Math.ceil((l.x1 + hw - x0) / cs));
        var r0 = Math.max(0, Math.floor((l.y0 - hh - y0) / cs)), r1 = Math.min(rowsN - 1, Math.ceil((l.y1 + hh - y0) / cs));
        for (var r = r0; r <= r1; r++) for (var c = c0; c <= c1; c++) {
          var cx = x0 + (c + 0.5) * cs, cy = y0 + (r + 0.5) * cs, k = r * cols + c;
          if (cx + hw > l.x0 && cx - hw < l.x1 && cy + hh > l.y0 && cy - hh < l.y1 && block[k] < v) block[k] = v;
        }
      }
      lines.forEach(function (l) { mark(l, hwS, hhS, 1); });
      lines.forEach(function (l) { mark(l, hwH, hhH, 2); });
      // being on screen is better than flying past its edge
      for (var c = 0; c < cols; c++) {
        var cx = x0 + (c + 0.5) * cs;
        var off = (cx - beeSize * 0.45 < screenL || cx + beeSize * 0.45 > screenR) ? 5 : 1;
        for (var r = 0; r < rowsN; r++) cost[r * cols + c] = off;
      }
      var N = cols * rowsN;
      grid = { cs: cs, x0: x0, y0: y0, cols: cols, rows: rowsN, block: block, cost: cost, hw: hwH,
        gs: new Float32Array(N), from: new Int32Array(N), closed: new Uint8Array(N) }; // search buffers, reused
    }
    function cellOf(p) { return { c: clamp(Math.floor((p.x - grid.x0) / grid.cs), 0, grid.cols - 1), r: clamp(Math.floor((p.y - grid.y0) / grid.cs), 0, grid.rows - 1) }; }
    function centerOf(c, r) { return { x: grid.x0 + (c + 0.5) * grid.cs, y: grid.y0 + (r + 0.5) * grid.cs }; }
    // soft check (planning, hovering) by default; hard = only the cells where she would touch a line
    function freeAt(p, hard) {
      var c = Math.floor((p.x - grid.x0) / grid.cs), r = Math.floor((p.y - grid.y0) / grid.cs);
      if (c < 0 || r < 0 || c >= grid.cols || r >= grid.rows) return false;
      var v = grid.block[r * grid.cols + c];
      return hard ? v < 2 : v === 0;
    }
    // the nearest free point to p (searching outward ring by ring)
    function nearestFree(p) {
      if (freeAt(p)) return p;
      var k = cellOf(p);
      for (var d = 1; d < 60; d++) {
        var best = null, bd = 1e9;
        for (var dr = -d; dr <= d; dr++) for (var dc = -d; dc <= d; dc++) {
          if (Math.max(Math.abs(dr), Math.abs(dc)) !== d) continue;
          var c = k.c + dc, r = k.r + dr;
          if (c < 0 || r < 0 || c >= grid.cols || r >= grid.rows || grid.block[r * grid.cols + c]) continue;
          var q = centerOf(c, r), dd = (q.x - p.x) * (q.x - p.x) + (q.y - p.y) * (q.y - p.y) * 0.6;
          if (dd < bd) { bd = dd; best = q; }
        }
        if (best) return best;
      }
      return p;
    }

    // A* over the cells (8 directions), limited to a band of the page around start and goal
    function plan(a, b) {
      var s = cellOf(a), g = cellOf(nearestFree(b));
      var cols = grid.cols, block = grid.block, cost = grid.cost;
      var rMin = Math.max(0, Math.min(s.r, g.r) - 90), rMax = Math.min(grid.rows - 1, Math.max(s.r, g.r) + 90);
      var gs = grid.gs, from = grid.from, closed = grid.closed;
      for (var i = rMin * cols, e = (rMax + 1) * cols; i < e; i++) { gs[i] = 1e9; from[i] = -1; closed[i] = 0; }
      var start = s.r * cols + s.c, goal = g.r * cols + g.c;
      var heap = [], hf = [];
      function push(n, f) { heap.push(n); hf.push(f); var i = heap.length - 1; while (i > 0) { var p = (i - 1) >> 1; if (hf[p] <= hf[i]) break; var t = heap[p]; heap[p] = heap[i]; heap[i] = t; t = hf[p]; hf[p] = hf[i]; hf[i] = t; i = p; } }
      function pop() { var top = heap[0], ln = heap.pop(), lf = hf.pop(); if (heap.length) { heap[0] = ln; hf[0] = lf; var i = 0; for (;;) { var l = 2 * i + 1, r = l + 1, m = i; if (l < heap.length && hf[l] < hf[m]) m = l; if (r < heap.length && hf[r] < hf[m]) m = r; if (m === i) break; var t = heap[m]; heap[m] = heap[i]; heap[i] = t; t = hf[m]; hf[m] = hf[i]; hf[i] = t; i = m; } } return top; }
      function hcost(n) { var dc = Math.abs(n % cols - g.c), dr = Math.abs(((n / cols) | 0) - g.r); return (dc + dr) + (1.414 - 2) * Math.min(dc, dr); }
      gs[start] = 0; push(start, hcost(start));
      var DC = [1, -1, 0, 0, 1, 1, -1, -1], DR = [0, 0, 1, -1, 1, -1, 1, -1], steps = 0;
      while (heap.length && steps++ < 150000) {
        var n = pop();
        if (closed[n]) continue; closed[n] = 1;
        if (n === goal) break;
        var nc = n % cols, nr = (n / cols) | 0;
        for (var k = 0; k < 8; k++) {
          var c = nc + DC[k], r = nr + DR[k];
          if (c < 0 || c >= cols || r < rMin || r > rMax) continue;
          var m = r * cols + c;
          if (block[m] || closed[m]) continue;
          if (k >= 4 && (block[nr * cols + c] || block[r * cols + nc])) continue; // no cutting corners
          var ng = gs[n] + (k >= 4 ? 1.414 : 1) * cost[m];
          if (ng < gs[m]) { gs[m] = ng; from[m] = n; push(m, ng + hcost(m)); }
        }
      }
      if (from[goal] === -1 && goal !== start) return null;
      var path = [], cur = goal;
      while (cur !== -1) { path.push(centerOf(cur % cols, (cur / cols) | 0)); if (cur === start) break; cur = from[cur]; }
      path.reverse();
      path[path.length - 1] = nearestFree(b);
      return smooth(path);
    }
    // straighten the path wherever there is a clear line of sight
    function clear(a, b) {
      var d = Math.hypot(b.x - a.x, b.y - a.y), n = Math.ceil(d / (grid.cs * 0.5));
      for (var i = 1; i < n; i++) { var t = i / n; if (!freeAt({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t })) return false; }
      return true;
    }
    function smooth(path) {
      if (path.length < 3) return path;
      var out = [path[0]], i = 0;
      while (i < path.length - 1) {
        var j = path.length - 1;
        while (j > i + 1 && !clear(path[i], path[j])) j--;
        out.push(path[j]); i = j;
      }
      return out;
    }

    /* ---------- where she wants to be ---------- */
    // The reading line is ~45% down the screen. Priority: beside a step number that is at the reading
    // line; on a photo that spans it; otherwise in the margin beside the text, on the side she is on.
    var side = RTL ? -1 : 1; // -1 left margin, +1 right margin
    function laneX(s) {
      var hw = grid ? grid.hw + 10 : beeSize * 0.6;
      return s < 0 ? -hw : W + hw;
    }
    function goalFor(eye) {
      var hh = beeH * INK.h / 2 + 12, hw = grid ? grid.hw + 4 : beeSize * 0.5, i, r;
      if (!rows.length) return { x: laneX(RTL ? -1 : 1), y: eye, follow: true };
      if (!narrow) {
        // Wide screens: she keeps to ONE side of the page (right in English, left in Arabic), so she
        // never has to cross the words. Down that margin she weaves gently; where a step's words are
        // on her side she settles beside its number; where a photo is on her side she drifts onto it.
        var sd = RTL ? -1 : 1, cur = 0;
        for (i = 0; i < rows.length; i++) if (eye >= Math.min(rows[i].text.y, rows[i].media.y) - 60) cur = i;
        r = rows[cur];
        var room = sd < 0 ? -screenL - (grid ? grid.hw : 0) * 2 - 20 : screenR - W - (grid ? grid.hw : 0) * 2 - 20;
        var swing = clamp(room, 0, 70);
        var lane = { x: laneX(sd) + sd * swing * 0.5 * (1 + Math.sin(eye / 170)), y: eye, follow: true };
        var textMine = sd < 0 ? r.textLeft : !r.textLeft;
        if (textMine) {
          if (Math.abs(eye - r.numY) < 90) return { x: laneX(sd), y: r.numY, follow: false };
          return lane;
        }
        var m = r.media;
        if (eye > m.y + hh * 0.5 && eye < m.b - hh * 0.5) {
          return { x: sd > 0 ? m.x + m.w * 0.72 : m.x + m.w * 0.28, y: clamp(eye, m.y + hh, m.b - hh), follow: true };
        }
        return lane;
      }
      // phones: she stays on the pictures. On the picture at the reading line she keeps pace with it;
      // while the words between two pictures are read she waits at the nearer picture's edge (the one
      // above until halfway down the words, then the one below), flying round the words along the
      // screen edge to get there
      var pics = rows.map(function (q) { return q.media; });
      if (hero) pics.unshift(hero);
      var px = function (m) { return m.x + m.w * (RTL ? 0.25 : 0.75); };
      for (i = 0; i < pics.length; i++) {
        var m = pics[i], nx = pics[i + 1];
        if (eye < m.y + hh * 0.4 && i === 0) return { x: px(m), y: m.y + hh, follow: false };
        if (eye <= m.b - hh * 0.4) return { x: m.x + m.w * (RTL ? 0.3 : 0.7), y: clamp(eye, m.y + hh, m.b - hh), follow: true };
        if (!nx) return { x: px(m), y: m.b - hh, follow: false };
        if (eye < nx.y + hh * 0.4) {
          return eye < (m.b + nx.y) / 2 ? { x: px(m), y: m.b - hh, follow: false } : { x: px(nx), y: nx.y + hh, follow: false };
        }
      }
      return { x: laneX(1), y: eye, follow: true };
    }

    /* ---------- flight ---------- */
    var pos = null, vel = { x: 0, y: 0 }, goal = null, path = null, pIdx = 0, lastPlan = 0, t0 = 0, eyeV = 0, lastEye = null, stuckUntil = 0;
    var face = 1, faceWant = 1, tilt = 0, heading = Math.PI / 2, flip = 1, wakeA = 0;
    var last = 0, prevPos = null, sparkT = 0, sparks = 0, raf = 0, inView = true, lastSpeed = 0, lastP = null, lastTail = null;

    function eyeY() { return window.innerHeight * 0.45 - inner.getBoundingClientRect().top; }

    function retarget(now, force) {
      var raw = goalFor(eyeY());
      var g = nearestFree(raw); g.follow = raw.follow;
      var moved = !goal || Math.hypot(g.x - goal.x, g.y - goal.y) > 24;
      if (force || (moved && now - lastPlan > 120)) {
        var p = plan(pos, g);
        if (p) { path = p; pIdx = 1; goal = g; lastPlan = now; }
        else if (goal) goal.follow = false;
      } else if (goal) goal.follow = g.follow;
    }

    function step(now, dt) {
      if (!pos) return false;
      var f = dt / 16.7;
      // when she has arrived, she hovers: a slow, small figure-of-eight around her spot
      var target, endDist = goal ? Math.hypot(goal.x - pos.x, goal.y - pos.y) : 0;
      if (path && pIdx < path.length) {
        while (pIdx < path.length - 1 && Math.hypot(path[pIdx].x - pos.x, path[pIdx].y - pos.y) < grid.cs * 2.2) pIdx++;
        target = path[pIdx];
      } else target = goal || pos;
      var hover = endDist < 30 && pIdx >= (path ? path.length - 1 : 0);
      if (hover && goal) {
        var tt = (now - t0) / 1000;
        var hp = { x: goal.x + Math.sin(tt * 0.9) * 10, y: goal.y + Math.sin(tt * 1.8) * 6 };
        if (freeAt(hp, true)) target = hp;
      }
      var dx = target.x - pos.x, dy = target.y - pos.y, d = Math.hypot(dx, dy) || 1;
      // speed: calm when close, quicker when she has far to go (so she keeps up with fast scrolling)
      var far = path ? Math.hypot(goal.x - pos.x, goal.y - pos.y) : d;
      var maxS = clamp(2.6 + far / 32, 2.6, narrow ? 16 : 18);
      var lastLeg = !path || pIdx >= path.length - 1;
      var speed = lastLeg ? Math.min(maxS, d / 9) : maxS;
      var want = { x: dx / d * speed, y: dy / d * speed };
      // she anticipates the scroll: while following the reading line she also moves with it
      if (goal && goal.follow && lastLeg) want.y += eyeV;
      // she accelerates harder when she has further to go (a quick dart round the words, a gentle drift nearby)
      var maxA = (hover ? 0.12 : 0.3 + maxS * 0.06) * f;
      var sx = want.x - vel.x, sy = want.y - vel.y, sm = Math.hypot(sx, sy);
      if (sm > maxA) { sx = sx / sm * maxA; sy = sy / sm * maxA; }
      vel.x += sx; vel.y += sy;
      var nx = pos.x + vel.x * f, ny = pos.y + vel.y * f;
      // never into a blocked cell: slide along it, or stop
      if (freeAt({ x: nx, y: ny }, true)) { pos = { x: nx, y: ny }; }
      else if (freeAt({ x: nx, y: pos.y }, true)) { pos = { x: nx, y: pos.y }; vel.y *= 0.3; }
      else if (freeAt({ x: pos.x, y: ny }, true)) { pos = { x: pos.x, y: ny }; vel.x *= 0.3; }
      else { vel.x *= 0.3; vel.y *= 0.3; if (now > stuckUntil) { stuckUntil = now + 200; retarget(now, true); } }
      return Math.hypot(vel.x, vel.y) > 0.05 || !hover;
    }

    function tailAt(p, f) {
      if (pose !== 'flying') return { x: p.x, y: p.y + beeSize * 0.18 };
      var lx = TAIL.x * beeSize * f, ly = TAIL.y * beeH, r = tilt * Math.PI / 180;
      return { x: p.x + lx * Math.cos(r) - ly * Math.sin(r), y: p.y + lx * Math.sin(r) + ly * Math.cos(r) };
    }
    function norm(d) { while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI; return d; }

    function place(p, dt) {
      var vx = 0, vy = 0;
      if (prevPos && dt) { vx = (p.x - prevPos.x) / dt * 16; vy = (p.y - prevPos.y) / dt * 16; }
      prevPos = { x: p.x, y: p.y }; lastP = prevPos;
      var speed = Math.hypot(vx, vy); lastSpeed = speed;
      // she faces where she flies; resting, she turns to face the page so her ribbon flows outward
      var restFace = p.x < W / 2 ? 1 : -1;
      var want = vx > 0.7 ? 1 : vx < -0.7 ? -1 : speed < 0.3 ? restFace : face > 0 ? 1 : -1;
      faceWant = want;
      face += (want - face) * (dt ? 1 - Math.exp(-dt * (speed < 0.3 ? 0.01 : 0.03)) : 1);
      if (pose === 'flying') {
        // banking: she leans into the way she is going, a little nose-down when diving, level at rest
        var want_r = 0;
        if (speed > 0.3) {
          var th = Math.atan2(vy, Math.abs(vx) + 10) * 180 / Math.PI;
          want_r = clamp(th * 0.5, -MAXT, MAXT) * (face > 0 ? 1 : -1);
        }
        tilt += (want_r - tilt) * (dt ? 1 - Math.exp(-dt * 0.006) : 1);
      } else {
        tilt += (clamp(vx * 1.6, -14, 14) - tilt) * (dt ? 1 - Math.exp(-dt * 0.012) : 1);
      }
      var f = Math.abs(face) < 0.08 ? (face < 0 ? -0.08 : 0.08) : face;
      bee.style.transform = 'translate3d(' + (p.x - beeSize / 2).toFixed(1) + 'px,' + (p.y - beeH / 2).toFixed(1) + 'px,0) rotate(' + tilt.toFixed(2) + 'deg) scaleX(' + f.toFixed(3) + ')';
      if (speed > 1.2) bee.classList.add('is-flying'); else if (speed < 0.4) bee.classList.remove('is-flying');

      // the crest ribbon: always there at her tail; streams away from her travel, lies out behind her at rest
      var moving = speed > 0.3;
      var target = moving ? Math.atan2(vy, vx) : (face > 0 ? 0 : Math.PI);
      heading += norm(target - heading) * (dt ? 1 - Math.exp(-dt * (moving ? 0.008 : 0.0035)) : 1);
      var c = Math.cos(heading), wantFlip = c < -0.25 ? -1 : c > 0.25 ? 1 : (flip < 0 ? -1 : 1);
      flip += (wantFlip - flip) * (dt ? 1 - Math.exp(-dt * 0.006) : 1);
      var wantA = clamp((speed - 0.3) / 3, 0, 1);
      wakeA += (wantA - wakeA) * (dt ? 1 - Math.exp(-dt * (wantA > wakeA ? 0.01 : 0.004)) : 1);
      if (wake) {
        var rot = heading * 180 / Math.PI - 31 * (flip < 0 ? -1 : 1);
        var fy = Math.abs(flip) < 0.06 ? (flip < 0 ? -0.06 : 0.06) : flip;
        wake.style.opacity = '1';
        var tp = tailAt(p, f); lastTail = tp;
        wake.style.transform = 'translate3d(' + tp.x.toFixed(1) + 'px,' + tp.y.toFixed(1) + 'px,0) rotate(' + rot.toFixed(1) + 'deg) scale(' + (0.85 + 0.3 * wakeA).toFixed(3) + ',' + fy.toFixed(3) + ')';
      }
      if (!reduce && speed > 1.5 && sparks < 22) {
        sparkT += dt;
        if (sparkT > 70) { sparkT = 0; spark(tailAt(p, f), false); }
      }
    }

    function spark(p, idle) {
      if (!fxTw.length || !fly) return;
      var isStar = fxStar && !idle && Math.random() < 0.14;
      var s = document.createElement('img');
      s.className = 'osy-spark';
      s.alt = ''; s.decoding = 'async';
      s.src = isStar ? fxStar : fxTw[(Math.random() * fxTw.length) | 0];
      var back = heading + Math.PI, bx = Math.cos(back), by = Math.sin(back), px = -by, py = bx;
      var d0 = idle ? 0.15 + Math.random() * 0.3 : 0.35 + Math.random() * 0.55;
      var sd = (Math.random() - 0.5) * (idle ? 0.9 : 0.5);
      var x = p.x + (bx * d0 + px * sd) * beeSize, y = p.y + (by * d0 + py * sd) * beeSize;
      var travel = idle ? 10 + Math.random() * 10 : 22 + Math.random() * 34;
      var size = beeSize * (isStar ? 0.2 + Math.random() * 0.07 : 0.1 + Math.random() * 0.07);
      s.style.left = x.toFixed(1) + 'px'; s.style.top = y.toFixed(1) + 'px'; s.style.width = size.toFixed(1) + 'px';
      s.style.setProperty('--dx', (idle ? (Math.random() - 0.5) * 12 : bx * travel + px * (Math.random() - 0.5) * 14).toFixed(1) + 'px');
      s.style.setProperty('--dy', (idle ? 8 + Math.random() * 10 : by * travel + 8 + Math.random() * 14).toFixed(1) + 'px');
      s.style.setProperty('--rot', ((Math.random() - 0.5) * 300).toFixed(0) + 'deg');
      s.style.setProperty('--dur', (1.1 + Math.random() * 0.6).toFixed(2) + 's');
      sparks++;
      s.addEventListener('animationend', function () { sparks--; s.remove(); });
      fly.appendChild(s);
    }
    setInterval(function () {
      if (reduce || !inView || !lastP || lastSpeed > 0.4 || document.hidden) return;
      spark(lastTail || lastP, true);
    }, 520);

    // the step numbers light up as the reader reaches them
    function lightSteps() {
      var y = eyeY();
      rows.forEach(function (r) { if (r.num) r.num.classList.toggle('is-lit', y >= r.numY - 60); });
    }

    /* ---------- characters on scroll ---------- */
    function moveChars() {
      if (reduce) return;
      var vh = window.innerHeight;
      visible.forEach(function (c) {
        var m = c.getAttribute('data-motion'), depth = parseFloat(c.getAttribute('data-depth')) || 0;
        var host = c.parentElement, r = host.getBoundingClientRect();
        var p = clamp((vh - r.top) / (vh + r.height), 0, 1), dir = RTL ? -1 : 1, tx = 0, ty = (0.5 - p) * depth;
        if (m === 'glide') { tx = (p - 0.5) * r.width * 0.55 * dir; ty -= p * 24; }
        else if (m === 'walk') { var cw = c.offsetWidth; tx = (-0.08 * r.width + p * (r.width - cw * 0.85)) * dir; }
        c.style.transform = 'translate3d(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px,0)';
      });
    }

    function frame(now) {
      raf = 0;
      var dt = last ? Math.min(now - last, 50) : 16; last = now;
      if (!t0) t0 = now;
      if (ready && !reduce && pos) {
        var ey = eyeY();
        if (lastEye !== null) eyeV += (((ey - lastEye) / dt * 16.7) - eyeV) * 0.25;
        lastEye = ey;
        retarget(now, false);
        step(now, dt);
        place(pos, dt);
        lightSteps();
      }
      moveChars();
      if (inView && !document.hidden && !reduce) raf = requestAnimationFrame(frame);
      else last = 0;
    }
    function kick() { if (!raf && inView && !document.hidden) raf = requestAnimationFrame(frame); }

    window.addEventListener('scroll', kick, { passive: true });
    document.addEventListener('visibilitychange', function () { last = 0; kick(); });
    function remeasure() {
      if (!pos) { start(); return; }
      measure();
      if (!ready) return;
      pos = pos ? nearestFree(pos) : null;
      goal = null; path = null;
      kick();
    }
    window.addEventListener('resize', function () { requestAnimationFrame(remeasure); });
    if ('ResizeObserver' in window) {
      var pending = 0;
      new ResizeObserver(function () {
        if (pending) return;
        pending = requestAnimationFrame(function () { pending = 0; remeasure(); });
      }).observe(inner);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) { inView = e[0].isIntersecting; if (inView) { last = 0; kick(); } }).observe(root);
    }
    if (mq.addEventListener) mq.addEventListener('change', function (e) { reduce = e.matches; start(); });

    function start() {
      measure();
      if (!ready) return;
      prevPos = null;
      if (reduce) {
        // resting beside the title, nothing moves
        var s = titleBox ? { x: laneX(RTL ? 1 : -1), y: titleBox.y + titleBox.h / 2 } : { x: laneX(1), y: 0 };
        pos = nearestFree(s); place(pos, 0); return;
      }
      if (!pos) {
        var g0 = nearestFree(goalFor(eyeY()));
        pos = { x: g0.x, y: g0.y }; goal = g0; path = [g0]; pIdx = 0;
        place(pos, 0);
      }
      kick();
    }
    if (document.readyState === 'complete') start();
    else { start(); window.addEventListener('load', remeasure); }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);
    // test hook: lets the page's own checks read the bee's state (no effect on visitors)
    root.__osyDebug = function () { return { mode: narrow ? 'phone' : 'wide', beeSize: beeSize, beeH: beeH, pos: pos, goal: goal, path: path, grid: grid && { cols: grid.cols, rows: grid.rows, cs: grid.cs }, freeAt: freeAt }; };
  }

  function boot() { Array.prototype.forEach.call(document.querySelectorAll('[data-osy-alive]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
